'use client';

import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { coalition } from '@explorer/themes/presets';

// consts
const BE_THEME = 'BE_THEME';
const DEFAULT_THEME: AvailableThemes = 'coalition';

// types
export type ThemeContextType = {
  THEMES: ThemesListType;
  defaultTheme: AppTheme;
  activeTheme: AppTheme;
  activeThemeSet: Dispatch<SetStateAction<AppTheme>>;
  setLocalThemeId: (themeId: AvailableThemes) => void;
  getLocalTheme: () => AppTheme;
  setActiveThemeById: (themeId: AvailableThemes) => void;
};

// init
const initialState: ThemeContextType = {
  THEMES: {
    coalition: coalition.light,
  },
  defaultTheme: coalition.light,
  activeTheme: coalition.light,
  activeThemeSet: () => undefined,
  setLocalThemeId: () => undefined,
  getLocalTheme: () => coalition.light,
  setActiveThemeById: () => undefined,
};

// create
export const ThemeContext = createContext<ThemeContextType>(initialState);

const readStoredThemeId = (): AvailableThemes | null => {
  try {
    return localStorage.getItem(BE_THEME) as AvailableThemes | null;
  } catch {
    return null;
  }
};

// provider hook
export const useThemeContextProvider = ({
  THEMES,
}: {
  THEMES: ThemeContextType['THEMES'];
}): ThemeContextType => {
  // memo default theme
  const defaultTheme = useMemo<AppTheme>(
    () => THEMES[DEFAULT_THEME]!,
    [THEMES],
  );

  // active theme state
  const [activeTheme, activeThemeSet] = useState<AppTheme>(defaultTheme);

  // Skip the first persist so the default light theme cannot overwrite a
  // stored dark-mode preference on mount / refresh.
  const hasCompletedInitialPersist = useRef(false);

  // force set theme id in ls
  const setLocalThemeId = useCallback<ThemeContextType['setLocalThemeId']>(
    (themeId) => {
      try {
        localStorage.setItem(BE_THEME, themeId);
      } catch {
        // localStorage can be missing during SSR or blocked by privacy settings
      }
    },
    [],
  );

  // get ls theme id
  const getLocalTheme = useCallback<ThemeContextType['getLocalTheme']>(() => {
    const localTheme = readStoredThemeId();
    if (localTheme && THEMES[localTheme]) {
      return THEMES[localTheme];
    }
    return defaultTheme;
  }, [THEMES, defaultTheme]);

  // force set theme in app by id
  const setActiveThemeById = useCallback<
    ThemeContextType['setActiveThemeById']
  >(
    (id: AvailableThemes) => {
      return THEMES[id] || defaultTheme;
    },
    [THEMES, defaultTheme],
  );

  // Restore the stored theme after mount. Do not persist here — the default
  // light state would overwrite a saved dark preference before restore runs.
  useEffect(() => {
    activeThemeSet(getLocalTheme());
    // Restore only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasCompletedInitialPersist.current) {
      hasCompletedInitialPersist.current = true;
      return;
    }
    setLocalThemeId(activeTheme.name as AvailableThemes);
  }, [activeTheme, setLocalThemeId]);

  // bundle provider value
  const providerValue = useMemo<ThemeContextType>(
    () => ({
      THEMES,
      defaultTheme,
      activeTheme,
      activeThemeSet,
      setLocalThemeId,
      getLocalTheme,
      setActiveThemeById,
    }),
    [
      THEMES,
      defaultTheme,
      activeTheme,
      activeThemeSet,
      setLocalThemeId,
      getLocalTheme,
      setActiveThemeById,
    ],
  );

  return providerValue;
};

// use context hook
export const useThemeContext = (): ThemeContextType => useContext(ThemeContext);
